/**
 * dictStore.ts — 全局动态数据字典 Reactive Store
 *
 * 设计原则：
 * 1. 应用启动时一次性拉取全部字典 (batch-items API)
 * 2. localStorage 二级缓存，避免每次刷新重载
 * 3. 提供 useDictMap / useDictOptions / getDictLabel / getDictTag 工具
 * 4. 后端不可用时降级使用内置 fallback 数据
 */

import { reactive, computed } from 'vue'
import { getDictBatchItems } from '../api/sys'

// ===== 类型定义 =====

export interface DictItemExtConfig {
  type?: string    // Element Plus tag type: success/warning/danger/info/primary/''
  color?: string   // 十六进制颜色
  icon?: string    // 图标名（保留）
}

export interface DictItem {
  value: string
  label: string
  sortOrder: number
  extConfig: DictItemExtConfig
}

const SALARY_MODE_OVERRIDE: DictItem[] = [
  { value: 'daily_plus_piece', label: '日薪+计件', sortOrder: 1, extConfig: { type: 'primary', color: '#409EFF' } },
  { value: 'monthly_only', label: '纯月薪', sortOrder: 2, extConfig: { type: 'warning', color: '#E6A23C' } },
  { value: 'piece_only', label: '纯计件', sortOrder: 3, extConfig: { type: 'success', color: '#67C23A' } },
]

const STATUS_MATRIX_OVERRIDES: Record<string, DictItem[]> = {
  mo_status: [
    { value: 'pending', label: '待排产', sortOrder: 1, extConfig: { type: 'info', color: '#909399' } },
    { value: 'planned', label: '已排产', sortOrder: 2, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'scheduled', label: '已排产', sortOrder: 3, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'released', label: '已排产', sortOrder: 4, extConfig: { type: 'primary', color: '#409EFF' } },
    { value: 'in_production', label: '生产中', sortOrder: 5, extConfig: { type: 'primary', color: '#409EFF' } },
    { value: 'in_progress', label: '生产中', sortOrder: 6, extConfig: { type: 'primary', color: '#409EFF' } },
    { value: 'partial_completed', label: '部分完工', sortOrder: 7, extConfig: { type: 'warning', color: '#F59E0B' } },
    { value: 'inbound_pending', label: '部分完工', sortOrder: 8, extConfig: { type: 'warning', color: '#F59E0B' } },
    { value: 'completed', label: '已完工', sortOrder: 9, extConfig: { type: 'success', color: '#67C23A' } },
    { value: 'closed', label: '已结案', sortOrder: 10, extConfig: { type: 'info', color: '#6B7280' } },
    { value: 'paused', label: '暂停', sortOrder: 11, extConfig: { type: 'danger', color: '#EF4444' } },
  ],
  task_status: [
    { value: 'pending', label: '待安排', sortOrder: 1, extConfig: { type: 'info', color: '#909399' } },
    { value: 'scheduled', label: '已排产', sortOrder: 2, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'in_progress', label: '生产中', sortOrder: 3, extConfig: { type: 'primary', color: '#409EFF' } },
    { value: 'completed', label: '已完工', sortOrder: 4, extConfig: { type: 'success', color: '#67C23A' } },
    { value: 'skipped', label: '跳过', sortOrder: 5, extConfig: { type: 'info', color: '#C0C4CC' } },
    { value: 'outsource_pending', label: '外协待发出', sortOrder: 6, extConfig: { type: 'warning', color: '#9333EA' } },
    { value: 'transit_out', label: '外协加工中', sortOrder: 7, extConfig: { type: 'warning', color: '#9333EA' } },
    { value: 'dispatched', label: '外协已发料', sortOrder: 8, extConfig: { type: 'warning', color: '#F97316' } },
    { value: 'transit_in', label: '外协已收回', sortOrder: 9, extConfig: { type: 'primary', color: '#7C3AED' } },
  ],
  outsource_task_status: [
    { value: 'pending', label: '待发出', sortOrder: 1, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'outsource_pending', label: '待发出', sortOrder: 2, extConfig: { type: 'warning', color: '#9333EA' } },
    { value: 'transit_out', label: '加工中', sortOrder: 3, extConfig: { type: 'warning', color: '#9333EA' } },
    { value: 'transit_in', label: '已收回', sortOrder: 4, extConfig: { type: 'primary', color: '#7C3AED' } },
    { value: 'completed', label: '已完成', sortOrder: 5, extConfig: { type: 'success', color: '#67C23A' } },
  ],
  qc_status: [
    { value: 'pending_qc', label: '待检', sortOrder: 1, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'pending', label: '待检', sortOrder: 2, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'passed', label: '合格', sortOrder: 3, extConfig: { type: 'success', color: '#67C23A' } },
    { value: 'failed', label: '不合格', sortOrder: 4, extConfig: { type: 'danger', color: '#F56C6C' } },
    { value: 'concession', label: '让步通过', sortOrder: 5, extConfig: { type: 'warning', color: '#F59E0B' } },
    { value: 'conditional_pass', label: '让步通过', sortOrder: 6, extConfig: { type: 'warning', color: '#F59E0B' } },
    { value: 'rework', label: '返修中', sortOrder: 7, extConfig: { type: 'primary', color: '#409EFF' } },
    { value: 'rework_in_progress', label: '返修中', sortOrder: 8, extConfig: { type: 'primary', color: '#409EFF' } },
  ],
  rd_plan_status: [
    { value: 'draft', label: '立项', sortOrder: 1, extConfig: { type: 'info', color: '#909399' } },
    { value: 'planning', label: '设计中', sortOrder: 2, extConfig: { type: 'primary', color: '#409EFF' } },
    { value: 'designing', label: '设计中', sortOrder: 3, extConfig: { type: 'primary', color: '#409EFF' } },
    { value: 'sampling', label: '打样中', sortOrder: 4, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'prototyping', label: '打样中', sortOrder: 5, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'pending', label: '评审中', sortOrder: 6, extConfig: { type: 'warning', color: '#F59E0B' } },
    { value: 'reviewing', label: '评审中', sortOrder: 7, extConfig: { type: 'warning', color: '#F59E0B' } },
    { value: 'approved', label: '封样', sortOrder: 8, extConfig: { type: 'success', color: '#67C23A' } },
    { value: 'completed', label: '封样', sortOrder: 9, extConfig: { type: 'success', color: '#67C23A' } },
    { value: 'transferred', label: '转大货', sortOrder: 10, extConfig: { type: 'success', color: '#10B981' } },
    { value: 'mass_production', label: '转大货', sortOrder: 11, extConfig: { type: 'success', color: '#10B981' } },
    { value: 'cancelled', label: '取消', sortOrder: 12, extConfig: { type: 'danger', color: '#F56C6C' } },
  ],
  product_lifecycle: [
    { value: 'draft', label: '开发中', sortOrder: 1, extConfig: { type: 'info', color: '#909399' } },
    { value: 'sampling', label: '开发中', sortOrder: 2, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'confirmed', label: '可下单', sortOrder: 3, extConfig: { type: 'primary', color: '#409EFF' } },
    { value: 'active', label: '可下单', sortOrder: 4, extConfig: { type: 'success', color: '#67C23A' } },
    { value: 'discontinued', label: '停产', sortOrder: 5, extConfig: { type: 'danger', color: '#F56C6C' } },
    { value: 'archived', label: '停产', sortOrder: 6, extConfig: { type: 'info', color: '#6B7280' } },
  ],
  lifecycle_status: [
    { value: 'draft', label: '开发中', sortOrder: 1, extConfig: { type: 'info', color: '#909399' } },
    { value: 'development', label: '开发中', sortOrder: 2, extConfig: { type: 'info', color: '#909399' } },
    { value: 'sampling', label: '开发中', sortOrder: 3, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'confirmed', label: '可下单', sortOrder: 4, extConfig: { type: 'primary', color: '#409EFF' } },
    { value: 'active', label: '可下单', sortOrder: 5, extConfig: { type: 'success', color: '#67C23A' } },
    { value: 'mass_production', label: '可下单', sortOrder: 6, extConfig: { type: 'success', color: '#67C23A' } },
    { value: 'discontinued', label: '停产', sortOrder: 7, extConfig: { type: 'danger', color: '#F56C6C' } },
    { value: 'archived', label: '停产', sortOrder: 8, extConfig: { type: 'info', color: '#6B7280' } },
  ],
  inventory_status: [
    { value: 'available', label: '可用', sortOrder: 1, extConfig: { type: 'success', color: '#67C23A' } },
    { value: 'reserved', label: '预占', sortOrder: 2, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'in_use', label: '预占', sortOrder: 3, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'frozen', label: '冻结', sortOrder: 4, extConfig: { type: 'danger', color: '#F56C6C' } },
    { value: 'shipped', label: '已出库', sortOrder: 5, extConfig: { type: 'info', color: '#6B7280' } },
    { value: 'exhausted', label: '已出库', sortOrder: 6, extConfig: { type: 'info', color: '#6B7280' } },
  ],
  fabric_stock_status: [
    { value: 'available', label: '可用', sortOrder: 1, extConfig: { type: 'success', color: '#67C23A' } },
    { value: 'reserved', label: '预占', sortOrder: 2, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'in_use', label: '预占', sortOrder: 3, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'frozen', label: '冻结', sortOrder: 4, extConfig: { type: 'danger', color: '#F56C6C' } },
    { value: 'shipped', label: '已出库', sortOrder: 5, extConfig: { type: 'info', color: '#6B7280' } },
    { value: 'exhausted', label: '已出库', sortOrder: 6, extConfig: { type: 'info', color: '#6B7280' } },
  ],
}

const resolveDictItems = (dictCode: string): DictItem[] => {
  if (dictCode === 'salary_mode') {
    return SALARY_MODE_OVERRIDE
  }
  return dictCache[dictCode] || []
}

// ===== 内置 Fallback（后端不可用时的兜底数据，与种子 SQL 同步） =====
const FALLBACK_DICT: Record<string, DictItem[]> = {
  mo_status: [
    { value: 'planned', label: '已排产', sortOrder: 1, extConfig: { type: 'info', color: '#909399' } },
    { value: 'released', label: '已下达', sortOrder: 2, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'in_production', label: '生产中', sortOrder: 3, extConfig: { type: '', color: '#409EFF' } },
    { value: 'inbound_pending', label: '待入库', sortOrder: 4, extConfig: { type: 'warning', color: '#F59E0B' } },
    { value: 'completed', label: '已完成', sortOrder: 5, extConfig: { type: 'success', color: '#67C23A' } },
    { value: 'closed', label: '已结案', sortOrder: 6, extConfig: { type: 'info', color: '#6B7280' } },
  ],
  task_status: [
    { value: 'pending', label: '待安排', sortOrder: 1, extConfig: { type: 'info', color: '#909399' } },
    { value: 'scheduled', label: '已排程', sortOrder: 2, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'in_progress', label: '进行中', sortOrder: 3, extConfig: { type: '', color: '#409EFF' } },
    { value: 'completed', label: '已完成', sortOrder: 4, extConfig: { type: 'success', color: '#67C23A' } },
    { value: 'skipped', label: '跳过', sortOrder: 5, extConfig: { type: 'info', color: '#C0C4CC' } },
    { value: 'transit_out', label: '外协加工中', sortOrder: 6, extConfig: { type: 'warning', color: '#9333EA' } },
    { value: 'transit_in', label: '已收回', sortOrder: 7, extConfig: { type: '', color: '#7C3AED' } },
    { value: 'outsource_pending', label: '待发出', sortOrder: 8, extConfig: { type: 'warning', color: '#9333EA' } },
    { value: 'dispatched', label: '已发料', sortOrder: 9, extConfig: { type: '', color: '#F97316' } },
  ],
  qc_status: [
    { value: 'pending_qc', label: '待质检', sortOrder: 1, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'passed', label: '合格', sortOrder: 2, extConfig: { type: 'success', color: '#67C23A' } },
    { value: 'failed', label: '不合格', sortOrder: 3, extConfig: { type: 'danger', color: '#F56C6C' } },
  ],
  bundle_status: [
    { value: 'pending', label: '待裁剪', sortOrder: 1, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'created', label: '待打印', sortOrder: 2, extConfig: { type: 'info', color: '#909399' } },
    { value: 'printed', label: '已打印', sortOrder: 3, extConfig: { type: 'primary', color: '#409EFF' } },
    { value: 'in_process', label: '流转中', sortOrder: 4, extConfig: { type: '', color: '#409EFF' } },
    { value: 'completed', label: '已完成', sortOrder: 5, extConfig: { type: 'success', color: '#67C23A' } },
    { value: 'voided', label: '已作废', sortOrder: 6, extConfig: { type: 'danger', color: '#F56C6C' } },
  ],
  fqc_result: [
    { value: 'pass', label: '合格', sortOrder: 1, extConfig: { type: 'success', color: '#67C23A' } },
    { value: 'conditional_pass', label: '让步接收', sortOrder: 2, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'fail', label: '不合格', sortOrder: 3, extConfig: { type: 'danger', color: '#F56C6C' } },
  ],
  warehouse_type: [
    { value: 'raw_material', label: '原辅料仓', sortOrder: 1, extConfig: { type: 'primary' } },
    { value: 'semi_finished', label: '半成品仓', sortOrder: 2, extConfig: { type: 'warning' } },
    { value: 'finished', label: '成品仓', sortOrder: 3, extConfig: { type: 'success' } },
    { value: 'return', label: '退货仓', sortOrder: 4, extConfig: { type: 'danger' } },
    { value: 'outsource', label: '外协仓', sortOrder: 5, extConfig: { type: 'info' } },
  ],
  mr_status: [
    { value: 'unfulfilled', label: '未采购', sortOrder: 1, extConfig: { type: 'danger', color: '#F56C6C' } },
    { value: 'purchasing', label: '采购中', sortOrder: 2, extConfig: { type: 'primary', color: '#409EFF' } },
    { value: 'partial_arrived', label: '部分到货', sortOrder: 3, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'fulfilled', label: '已齐套', sortOrder: 4, extConfig: { type: 'success', color: '#67C23A' } },
  ],
  demand_priority: [
    { value: 'urgent', label: '急单必买', sortOrder: 1, extConfig: { type: 'danger', color: '#F56C6C' } },
    { value: 'normal', label: '常规需求', sortOrder: 2, extConfig: { type: 'primary', color: '#409EFF' } },
    { value: 'buffer', label: '备货可缓', sortOrder: 3, extConfig: { type: 'success', color: '#67C23A' } },
  ],
  so_status: [
    { value: 'draft', label: '草稿', sortOrder: 0, extConfig: { type: 'info', color: '#909399' } },
    { value: 'imported', label: '已导入', sortOrder: 1, extConfig: { type: 'info', color: '#909399' } },
    { value: 'cleaning', label: '清洗中', sortOrder: 2, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'approved', label: '已审批', sortOrder: 2, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'in_production', label: '生产中', sortOrder: 2, extConfig: { type: 'primary', color: '#409EFF' } },
    { value: 'ready', label: '待出库', sortOrder: 3, extConfig: { type: 'warning', color: '#F59E0B' } },
    { value: 'confirmed', label: '已确认', sortOrder: 3, extConfig: { type: 'primary', color: '#409EFF' } },
    { value: 'dispatched', label: '已排产', sortOrder: 4, extConfig: { type: 'success', color: '#67C23A' } },
    { value: 'partial_shipped', label: '部分出库', sortOrder: 5, extConfig: { type: 'warning', color: '#F59E0B' } },
    { value: 'shipped', label: '已出库', sortOrder: 6, extConfig: { type: 'success', color: '#67C23A' } },
    { value: 'completed', label: '已完成', sortOrder: 7, extConfig: { type: 'success', color: '#10B981' } },
  ],
  po_status: [
    { value: 'draft', label: '草稿', sortOrder: 1, extConfig: { type: 'info', color: '#909399' } },
    { value: 'pending', label: '待确认', sortOrder: 2, extConfig: { type: 'warning', color: '#F59E0B' } },
    { value: 'approved', label: '已审批', sortOrder: 3, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'purchasing', label: '采购中', sortOrder: 3, extConfig: { type: 'primary', color: '#409EFF' } },
    { value: 'partial', label: '部分到', sortOrder: 4, extConfig: { type: 'warning', color: '#F59E0B' } },
    { value: 'completed', label: '已完成', sortOrder: 5, extConfig: { type: 'success', color: '#67C23A' } },
    { value: 'cancelled', label: '已取消', sortOrder: 6, extConfig: { type: 'danger', color: '#F56C6C' } },
  ],
  rd_plan_status: [
    { value: 'draft', label: '草稿', sortOrder: 1, extConfig: { type: 'info', color: '#909399' } },
    { value: 'planning', label: '规划中', sortOrder: 2, extConfig: { type: 'info', color: '#909399' } },
    { value: 'pending', label: '待审批', sortOrder: 3, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'approved', label: '已批准', sortOrder: 4, extConfig: { type: 'primary', color: '#409EFF' } },
    { value: 'processing', label: '进行中', sortOrder: 5, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'in_progress', label: '进行中', sortOrder: 6, extConfig: { type: '', color: '#409EFF' } },
    { value: 'completed', label: '已完成', sortOrder: 7, extConfig: { type: 'success', color: '#67C23A' } },
    { value: 'cancelled', label: '已取消', sortOrder: 8, extConfig: { type: 'danger', color: '#F56C6C' } },
  ],
  design_wo_status: [
    { value: 'draft', label: '草稿', sortOrder: 1, extConfig: { type: 'info', color: '#909399' } },
    { value: 'designing', label: '设计中', sortOrder: 2, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'in_progress', label: '进行中', sortOrder: 3, extConfig: { type: '', color: '#409EFF' } },
    { value: 'reviewing', label: '评审中', sortOrder: 4, extConfig: { type: 'primary', color: '#409EFF' } },
    { value: 'completed', label: '已完成', sortOrder: 5, extConfig: { type: 'success', color: '#67C23A' } },
    { value: 'approved', label: '已定稿', sortOrder: 6, extConfig: { type: 'success', color: '#67C23A' } },
    { value: 'rejected', label: '已驳回', sortOrder: 7, extConfig: { type: 'danger', color: '#F56C6C' } },
  ],
  sample_wo_status: [
    { value: 'draft', label: '草稿', sortOrder: 1, extConfig: { type: 'info', color: '#909399' } },
    { value: 'pending', label: '待安排', sortOrder: 2, extConfig: { type: 'info', color: '#909399' } },
    { value: 'engineering', label: '工程准备', sortOrder: 3, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'producing', label: '打样中', sortOrder: 4, extConfig: { type: '', color: '#409EFF' } },
    { value: 'in_progress', label: '打样中', sortOrder: 5, extConfig: { type: '', color: '#409EFF' } },
    { value: 'qa', label: '质检中', sortOrder: 6, extConfig: { type: 'danger', color: '#F56C6C' } },
    { value: 'qa_checking', label: '质检中', sortOrder: 7, extConfig: { type: 'danger', color: '#F56C6C' } },
    { value: 'qa_pending', label: '待验收', sortOrder: 8, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'completed', label: '已完成', sortOrder: 9, extConfig: { type: 'success', color: '#67C23A' } },
    { value: 'cancelled', label: '已取消', sortOrder: 10, extConfig: { type: 'danger', color: '#F56C6C' } },
  ],
  wi_status: [
    { value: 'draft', label: '草稿', sortOrder: 1, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'published', label: '已发布', sortOrder: 2, extConfig: { type: 'success', color: '#67C23A' } },
    { value: 'archived', label: '已废弃', sortOrder: 3, extConfig: { type: 'info', color: '#909399' } },
  ],
  mfg_bom_status: [
    { value: '0', label: '未发布', sortOrder: 1, extConfig: { type: 'info', color: '#909399' } },
    { value: '1', label: '下发生产', sortOrder: 2, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: '2', label: '生产中', sortOrder: 3, extConfig: { type: 'success', color: '#67C23A' } },
    { value: '3', label: '已归档', sortOrder: 4, extConfig: { type: 'danger', color: '#F56C6C' } },
  ],
  lifecycle_status: [
    { value: 'concept', label: '概念阶段', sortOrder: 1, extConfig: { type: 'info', color: '#909399' } },
    { value: 'development', label: '开发中', sortOrder: 2, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'sampling', label: '打样中', sortOrder: 3, extConfig: { type: 'primary', color: '#409EFF' } },
    { value: 'pre_production', label: '试产', sortOrder: 4, extConfig: { type: 'warning', color: '#F59E0B' } },
    { value: 'mass_production', label: '量产中', sortOrder: 5, extConfig: { type: 'success', color: '#67C23A' } },
    { value: 'discontinued', label: '已停产', sortOrder: 6, extConfig: { type: 'danger', color: '#F56C6C' } },
  ],
  approval_status: [
    { value: '0', label: '待审核', sortOrder: 1, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: '1', label: '已通过', sortOrder: 2, extConfig: { type: 'success', color: '#67C23A' } },
    { value: '2', label: '已驳回', sortOrder: 3, extConfig: { type: 'danger', color: '#F56C6C' } },
  ],
  outsource_task_status: [
    { value: 'pending', label: '待发出', sortOrder: 1, extConfig: { type: 'info', color: '#909399' } },
    { value: 'outsource_pending', label: '待发出', sortOrder: 2, extConfig: { type: 'warning', color: '#9333EA' } },
    { value: 'transit_out', label: '加工中', sortOrder: 3, extConfig: { type: 'warning', color: '#9333EA' } },
    { value: 'transit_in', label: '已收回', sortOrder: 4, extConfig: { type: '', color: '#7C3AED' } },
    { value: 'completed', label: '已完成', sortOrder: 5, extConfig: { type: 'success', color: '#67C23A' } },
  ],
  picking_list_status: [
    { value: 'pending', label: '待发料', sortOrder: 1, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'partial', label: '部分发', sortOrder: 2, extConfig: { type: 'primary', color: '#409EFF' } },
    { value: 'issued', label: '已发齐', sortOrder: 3, extConfig: { type: 'success', color: '#67C23A' } },
    { value: 'closed', label: '已关闭', sortOrder: 4, extConfig: { type: 'info', color: '#909399' } },
  ],
  receive_status: [
    { value: 'pending', label: '待收货', sortOrder: 1, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'inspecting', label: '待复核', sortOrder: 2, extConfig: { type: 'primary', color: '#409EFF' } },
    { value: 'exception', label: '异常', sortOrder: 3, extConfig: { type: 'danger', color: '#F56C6C' } },
    { value: 'partial', label: '部分收', sortOrder: 4, extConfig: { type: 'primary', color: '#409EFF' } },
    { value: 'received', label: '已收货', sortOrder: 5, extConfig: { type: 'success', color: '#67C23A' } },
    { value: 'completed', label: '已收齐', sortOrder: 6, extConfig: { type: 'success', color: '#67C23A' } },
  ],
  prototype_wo_status: [
    { value: 'pending', label: '待排期', sortOrder: 1, extConfig: { type: 'info', color: '#909399' } },
    { value: 'cutting', label: '裁剪中', sortOrder: 2, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'sewing', label: '缝制中', sortOrder: 3, extConfig: { type: 'warning', color: '#F59E0B' } },
    { value: 'completed', label: '已完成', sortOrder: 4, extConfig: { type: 'success', color: '#67C23A' } },
    { value: 'approved', label: '已审核', sortOrder: 5, extConfig: { type: 'success', color: '#10B981' } },
    { value: 'rejected', label: '已驳回', sortOrder: 6, extConfig: { type: 'danger', color: '#F56C6C' } },
  ],
  product_lifecycle: [
    { value: 'draft', label: '研发中', sortOrder: 1, extConfig: { type: 'info', color: '#909399' } },
    { value: 'sampling', label: '打样中', sortOrder: 2, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'confirmed', label: '客户已确认', sortOrder: 3, extConfig: { type: 'primary', color: '#409EFF' } },
    { value: 'active', label: '正式大货', sortOrder: 4, extConfig: { type: 'success', color: '#67C23A' } },
  ],
  issue_station_status: [
    { value: 'pending', label: '待发料', sortOrder: 1, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'partial', label: '部分发', sortOrder: 2, extConfig: { type: 'primary', color: '#409EFF' } },
    { value: 'issued', label: '已发齐', sortOrder: 3, extConfig: { type: 'success', color: '#67C23A' } },
    { value: 'closed', label: '已关闭', sortOrder: 4, extConfig: { type: 'info', color: '#909399' } },
  ],
  bom_status: [
    { value: '0', label: '未发布', sortOrder: 1, extConfig: { type: 'info', color: '#909399' } },
    { value: '1', label: '待审核', sortOrder: 2, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: '2', label: '已审核', sortOrder: 3, extConfig: { type: 'success', color: '#67C23A' } },
    { value: '3', label: '已停用', sortOrder: 4, extConfig: { type: 'danger', color: '#F56C6C' } },
  ],
  employee_status: [
    { value: '0', label: '离职', sortOrder: 1, extConfig: { type: 'danger', color: '#F56C6C' } },
    { value: '1', label: '在职', sortOrder: 2, extConfig: { type: 'success', color: '#67C23A' } },
    { value: '2', label: '休假', sortOrder: 3, extConfig: { type: 'warning', color: '#E6A23C' } },
  ],
  // ===== 以下为 Phase 15 新增字典组 =====
  salary_mode: [
    { value: 'daily_plus_piece', label: '日薪+计件', sortOrder: 1, extConfig: { type: 'primary', color: '#409EFF' } },
    { value: 'monthly_only', label: '纯月薪', sortOrder: 2, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'piece_only', label: '纯计件', sortOrder: 3, extConfig: { type: 'success', color: '#67C23A' } },
  ],
  prototype_reason: [
    { value: 'customer_sample', label: '客户寄样', sortOrder: 1, extConfig: { type: 'primary', color: '#409EFF' } },
    { value: 'internal_review', label: '内部评审', sortOrder: 2, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'exhibition', label: '展会样品', sortOrder: 3, extConfig: { type: 'success', color: '#67C23A' } },
  ],
  inventory_trans_type: [
    { value: 'po_in', label: '采购入库', sortOrder: 1, extConfig: { type: 'success', color: '#67C23A' } },
    { value: 'prod_out', label: '生产领料', sortOrder: 2, extConfig: { type: 'primary', color: '#409EFF' } },
    { value: 'proto_out', label: '打样领料', sortOrder: 3, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'adjust', label: '盘点调整', sortOrder: 4, extConfig: { type: 'info', color: '#909399' } },
    { value: 'cutting_consume', label: '裁床扣料', sortOrder: 5, extConfig: { type: 'danger', color: '#F56C6C' } },
    { value: 'cutting_return', label: '裁床退料', sortOrder: 6, extConfig: { type: 'success', color: '#67C23A' } },
    { value: 'cutting_waste', label: '裁床损耗', sortOrder: 7, extConfig: { type: 'danger', color: '#F56C6C' } },
    { value: 'qc_return', label: '质检退回', sortOrder: 8, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'direct_in', label: '直接入库', sortOrder: 9, extConfig: { type: '', color: '#9333EA' } },
    { value: 'prod_return', label: '退料入库', sortOrder: 10, extConfig: { type: 'success', color: '#10B981' } },
    { value: 'count_in', label: '盘盈入库', sortOrder: 11, extConfig: { type: '', color: '#06B6D4' } },
    { value: 'rtv_out', label: '退货出库', sortOrder: 12, extConfig: { type: 'warning', color: '#F97316' } },
    { value: 'scrap_out', label: '报废出库', sortOrder: 13, extConfig: { type: 'danger', color: '#EF4444' } },
    { value: 'count_out', label: '盘亏出库', sortOrder: 14, extConfig: { type: 'info', color: '#6B7280' } },
    { value: 'fg_in', label: '成品入库', sortOrder: 15, extConfig: { type: 'success', color: '#10B981' } },
    { value: 'fg_out', label: '成品出库', sortOrder: 16, extConfig: { type: '', color: '#6366F1' } },
    { value: 'sales_out', label: '销售出库', sortOrder: 17, extConfig: { type: '', color: '#6366F1' } },
  ],
  material_category: [
    { value: 'fabric', label: '面料', sortOrder: 1, extConfig: { type: 'primary', color: '#409EFF' } },
    { value: 'accessory', label: '辅料', sortOrder: 2, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'trim', label: '花边饰品', sortOrder: 3, extConfig: { type: '', color: '#9333EA' } },
    { value: 'label', label: '标签吊牌', sortOrder: 4, extConfig: { type: 'info', color: '#909399' } },
    { value: 'packaging', label: '包装材料', sortOrder: 5, extConfig: { type: 'success', color: '#67C23A' } },
  ],
  skill_level: [
    { value: 'junior', label: '初级', sortOrder: 1, extConfig: { type: 'info', color: '#909399' } },
    { value: 'middle', label: '中级', sortOrder: 2, extConfig: { type: 'primary', color: '#409EFF' } },
    { value: 'senior', label: '高级', sortOrder: 3, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'expert', label: '技师', sortOrder: 4, extConfig: { type: 'success', color: '#67C23A' } },
  ],
  defect_type: [
    { value: 'skip_stitch', label: '跳线', sortOrder: 1, extConfig: { type: 'warning', color: '#E6A23C' } },
    { value: 'seam_burst', label: '爆缝', sortOrder: 2, extConfig: { type: 'danger', color: '#F56C6C' } },
    { value: 'color_diff', label: '色差', sortOrder: 3, extConfig: { type: 'warning', color: '#F59E0B' } },
    { value: 'stain', label: '污渍', sortOrder: 4, extConfig: { type: 'danger', color: '#EF4444' } },
    { value: 'size_deviation', label: '尺码偏差', sortOrder: 5, extConfig: { type: 'primary', color: '#409EFF' } },
    { value: 'other', label: '其他', sortOrder: 6, extConfig: { type: 'info', color: '#909399' } },
  ],
}

// ===== Reactive Store =====

const dictCache = reactive<Record<string, DictItem[]>>({})
let initialized = false

const applyFrozenStatusMatrix = () => {
  Object.entries(STATUS_MATRIX_OVERRIDES).forEach(([dictCode, frozenItems]) => {
    const existingItems = dictCache[dictCode] || []
    const existingMap = new Map(existingItems.map(item => [item.value, item]))
    const frozenValues = new Set(frozenItems.map(item => item.value))

    const mergedItems = frozenItems.map(item => {
      const existing = existingMap.get(item.value)
      return {
        ...existing,
        ...item,
        extConfig: {
          ...(existing?.extConfig || {}),
          ...(item.extConfig || {})
        }
      }
    })

    const legacyItems = existingItems.filter(item => !frozenValues.has(item.value))
    dictCache[dictCode] = [...mergedItems, ...legacyItems]
  })
}

/**
 * 初始化字典 — 在 main.ts 中 await 调用
 */
export async function initDictStore(): Promise<void> {
  if (initialized) return

  // 1. 先加载 fallback 作为底线保障
  Object.assign(dictCache, FALLBACK_DICT)
  applyFrozenStatusMatrix()

  // 2. 尝试从 localStorage 恢复（秒开）
  const cached = localStorage.getItem('__dict_cache__')
  if (cached) {
    try {
      const parsed = JSON.parse(cached)
      if (parsed && typeof parsed === 'object') {
        Object.assign(dictCache, parsed)
        applyFrozenStatusMatrix()
      }
    } catch { /* ignore corrupt cache */ }
  }

  // 3. 后台拉取最新数据（静默更新）
  try {
    const token = localStorage.getItem('token')
    if (token) {
      const res = await getDictBatchItems()
      if (res.code === 200 && res.data && Object.keys(res.data).length > 0) {
        // 用后端数据覆盖（保留 fallback 中后端没返回的 key）
        Object.assign(dictCache, res.data)
        
        // [修复] 将后端旧版数据结构平滑映射
        if (dictCache['mr_status']) {
          dictCache['mr_status'].forEach(item => {
            if (item.value === 'unpurchased') item.value = 'unfulfilled'
            if (item.label === '部分到') item.label = '部分到货'
          })
        }
        applyFrozenStatusMatrix()
        
        localStorage.setItem('__dict_cache__', JSON.stringify(dictCache))
      }
    }
  } catch {
    console.warn('[DictStore] 字典拉取失败，使用本地缓存/fallback')
  }

  initialized = true
}

/**
 * 强制刷新字典（供管理页面修改后调用）
 */
export async function refreshDictStore(): Promise<void> {
  try {
    const res = await getDictBatchItems()
    if (res.code === 200 && res.data) {
      Object.assign(dictCache, res.data)
      if (dictCache['mr_status']) {
        dictCache['mr_status'].forEach(item => {
          if (item.value === 'unpurchased') item.value = 'unfulfilled'
          if (item.label === '部分到') item.label = '部分到货'
        })
      }
      applyFrozenStatusMatrix()
      localStorage.setItem('__dict_cache__', JSON.stringify(dictCache))
    }
  } catch {
    console.warn('[DictStore] 字典刷新失败')
  }
}

// ===== 消费 API =====

/**
 * 获取某个字典的全部选项列表
 * 用于 <el-select> <el-option> 等场景
 */
export function useDictOptions(dictCode: string) {
  return computed(() => resolveDictItems(dictCode))
}

/**
 * 获取字典的 Map 结构: Record<value, DictItem>
 * 用于 statusMap[row.status]?.label 等状态渲染场景
 */
export function useDictMap(dictCode: string) {
  return computed<Record<string, DictItem>>(() => {
    const items = resolveDictItems(dictCode)
    const map: Record<string, DictItem> = {}
    for (const item of items) {
      map[item.value] = item
    }
    return map
  })
}

/**
 * 直接获取某个 value 的 label
 * 非响应式，用于纯函数/计算属性内部
 */
export function getDictLabel(dictCode: string, value: string | number | undefined): string {
  if (value === undefined || value === null) return ''
  const v = String(value)
  return resolveDictItems(dictCode).find(i => i.value === v)?.label || v
}

/**
 * 获取 Element Plus Tag 渲染属性
 * 返回 { label, type, color } 可直接绑定到 <el-tag>
 */
export function getDictTag(dictCode: string, value: string | number | undefined): { label: string; type: string; color: string } {
  if (value === undefined || value === null) return { label: '', type: 'info', color: '#909399' }
  const v = String(value)
  const item = resolveDictItems(dictCode).find(i => i.value === v)
  return {
    label: item?.label || v,
    type: item?.extConfig?.type || 'info',
    color: item?.extConfig?.color || '#909399',
  }
}
